import React, { useState, useEffect } from "react";
import axios from "axios";
import { ExternalLink } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { AIContent } from "../components/AIContent";
import { ArticleStrip } from "../components/ArticleStrip";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportsPage() {
  const [sports, setSports] = useState([]);
  const [family, setFamily] = useState("All");

  useEffect(() => {
    axios.get(`${API}/sports`).then((r) => setSports(r.data)).catch(() => {});
  }, []);

  const families = ["All", ...new Set(sports.map((s) => s.family))];
  const shown = family === "All" ? sports : sports.filter((s) => s.family === family);

  return (
    <div>
      <PageHero topic="sports stadium action" title="Explore Sports & Games" subtitle="A controlled global taxonomy of sports — team, individual, mind, adaptive and emerging — with governing bodies and major competitions." listenTargetId="page-content" />
      <div id="page-content" className="mx-auto max-w-7xl space-y-10 px-4 py-12 sm:px-6">
        <AIContent topic="The world's sports families — from team field sports to mind sports" context="Give an editorial overview of sport families: team field and court, racquet, athletics, aquatic, combat, mind and adaptive sports. Encourage local participation." />
        <ArticleStrip tag="Trends" title="Sport Features" limit={3} />
        <div className="flex flex-wrap gap-2" data-testid="sport-family-filters">
          {families.map((f) => (
            <button key={f} data-testid={`family-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setFamily(f)} className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${family === f ? "bg-[#0055FF] text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-[#0055FF] hover:text-[#0055FF]"}`}>
              {f === "All" ? <T>All</T> : <T>{f}</T>}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="sports-grid">
          {shown.map((s) => (
            <div key={s.id} data-testid={`sport-card-${s.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
              <div className="flex items-start justify-between">
                <span className="text-3xl">{s.icon}</span>
                <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase text-[#0055FF]">{s.type}</span>
              </div>
              <h3 className="mt-3 text-lg font-bold text-slate-900">{s.name}</h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400"><T>{s.family}</T></p>
              <p className="mt-3 text-sm leading-relaxed text-slate-600"><T>{s.description}</T></p>
              <div className="mt-4 space-y-1.5 border-t border-slate-100 pt-3 text-xs text-slate-500">
                <p><span className="font-bold text-slate-700"><T>Origin</T>:</span> {s.origin}</p>
                <p>
                  <span className="font-bold text-slate-700"><T>Governing body</T>:</span>{" "}
                  <a href={s.governing_body.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0055FF] hover:underline">
                    {s.governing_body.name} <ExternalLink className="h-3 w-3" />
                  </a>
                </p>
                <p><span className="font-bold text-slate-700"><T>Major events</T>:</span> {s.major_competitions.join(", ")}</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s.olympic && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Olympic</span>}
                {s.regions.map((r) => <span key={r} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{r}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
